<script>
    import EmptyState from '@itslearning/prometheus/assets/nodes/EmptyState/v1/EmptyState.svelte';
    import Button from '@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte';
    import IconButton from '@itslearning/prometheus/assets/inputs/IconButton/v1/IconButton.svelte';
    import Cell from '@itslearning/prometheus/assets/lists/Cell/v1/Cell.svelte';
    import CheckboxCell from '@itslearning/prometheus/assets/lists/CheckboxCell/v1/CheckboxCell.svelte';
    import Link from '@itslearning/prometheus/assets/Navigation/Link/v1/Link.svelte';
    import List from '@itslearning/prometheus/assets/lists/List/v1/List.svelte';
    import ListElement from '@itslearning/prometheus/assets/lists/ListElement/v1/ListElement.svelte';
    import TextCell from '@itslearning/prometheus/assets/lists/TextCell/v1/TextCell.svelte';
    import Modal from '@itslearning/prometheus/assets/modals/Modal/v2/Modal.svelte';
    import Banner from '@itslearning/prometheus/assets/feedback/Banner/v1/Banner.svelte';
    import DragHandle from '../DragAndDrop/DragHandle/DragHandle.svelte';
    import {navigateToEditQuestionPage, navigateToPreviewSurveyPage} from '../../navigation';
    import {deleteQuestions, saveQuestionOrder, hasRespondents} from '../../api';
    import {txt} from '../../i18n/i18n';
    import {survey, questions, navigation, currentQuestionIndex, permissions} from '../../stores';
    import {stripHtml} from '../../helpers';  

    // This focus "HACK" is required to make keyboard re-ordering work. Without this code, moving an element up in the list causes focus to be lost.
    import { afterUpdate } from 'svelte';
	afterUpdate(() => {
        if (questionToMove) {
            document.getElementById('drag-handle-' + questionToMove.questionId).focus();
        }
	});

    const warningText = txt('Unknown_error');
    let showDeleteWarning = false;
    let showNoQuestionsSelectedForDeleteAlert = false;
    let reorderInfo = '';
    let questionToMove = undefined;
    let destinationOrderNo = undefined;
    let originalPositions = undefined;
    let isDeleting = false;
    let errorFromApi = false;
    let respondentsStatus = undefined;

    $: if ($navigation.viewTabId === 'tabQuestions') {
        errorFromApi = false;
        getRespondentStatus();
    }
    $: allQuestionsSelected = $questions.find(q => q.isUiSelected === false) === undefined;
    $: selectedQuestionIds = $questions.filter(q => q.isUiSelected === true).map(q => q.questionId);
    $: hasGlobalResponse = respondentsStatus && respondentsStatus.hasGlobalResponse === true;
    $: enableModification = $permissions.canModify && respondentsStatus && !hasGlobalResponse;
    $: hasQuestions = $questions && $questions.length > 0;
    $: canPreviewSurvey = hasQuestions && $survey.answerId <= 0; // we don't want to enable the preview, since we use the store for the answer


    async function getRespondentStatus() {
        try {
            respondentsStatus = await hasRespondents();            
        } catch {
            // important that we don't call handleApiError here!
            errorFromApi = true;
         }
    }

    function shortQuestionTypeText(type) {
        if ('YesNo' === type) {
            return txt('QuestionType_YesNo_short');
        }
        if ('MultipleChoice' === type) {
            return txt('QuestionType_MultipleChoice_short');
        }
        if ('Matrix' === type) {
            return txt('Matrix_question');
        }
        if ('Open' === type) {
            return txt('Open_question');
        }
        return 'UNKNOWN question type';
    }

    function setOriginalPositions() {
        originalPositions = [];
        originalPositions.length = $questions.length;
        for (let i = 0; i < $questions.length; i++) {
            originalPositions[i] = $questions[i].questionId;
        }
    }

    function moveToTemp(destinationQuestionId) {
        if (destinationQuestionId === questionToMove.questionId) {
            return;
        }

        destinationOrderNo = $questions.find(q => q.questionId === destinationQuestionId).orderNo;
        const minOrderNo = Math.min(questionToMove.orderNo, destinationOrderNo);
        const maxOrderNo = Math.max(questionToMove.orderNo, destinationOrderNo);
        const move = destinationOrderNo > questionToMove.orderNo ? -1 : 1;
        for (let i = 0; i < $questions.length; i++) {
            const q = $questions[i];
            if (q.questionId !== questionToMove.questionId && q.orderNo >= minOrderNo && q.orderNo <= maxOrderNo) {
                q.orderNo += move;
            }
        }
        questionToMove.orderNo = destinationOrderNo;
        $questions.sort((a, b) => a.orderNo - b.orderNo);
        $questions = $questions;
    }

    function deleteSelectedQuestions() {
        if (selectedQuestionIds && selectedQuestionIds.length > 0) {
            showDeleteWarning = true;            
        } else {
            showNoQuestionsSelectedForDeleteAlert = true;
        }

    }

    async function performDelete() {
        showDeleteWarning = false;
        try {
            if (isDeleting) {
                return;
            }
            isDeleting = true;
            await deleteQuestions(selectedQuestionIds);            
            questions.set($questions.filter(q => q.isUiSelected === false));
            errorFromApi = false;
        } catch {
            handleApiError();
        }
        isDeleting = false;
    }

    function handleApiError() {        
        getRespondentStatus();  // just in case the problem is that there are now some answers
        errorFromApi = true;
    }

    async function finalizeMove() {
        if (!questionToMove || !destinationOrderNo) {
            questionToMove = undefined;
            destinationOrderNo = undefined;
            return;
        }
        try {
            const oldOrderNo = originalPositions.findIndex(id => id === questionToMove.questionId) + 1;
            if (oldOrderNo !== destinationOrderNo) {
                const result = await saveQuestionOrder(questionToMove.questionId, destinationOrderNo);
                errorFromApi = false;
                if (result && result.items && result.items.length > 0) {
                    for (let i = 0; i < result.items.length; i++) {
                        const q2 = $questions.find(q => q.questionId === result.items[i].id);
                        if (q2) {
                            q2.orderNo = result.items[i].orderNo;
                        }
                    }
                }
                $questions.sort((a, b) => a.orderNo - b.orderNo);
                $questions = $questions;
            }
        } catch {
            // reset order number back to how they were before move started
            $questions.forEach(q => {
                q.orderNo = originalPositions.findIndex(id => id === q.questionId) + 1; 
            });
            $questions.sort((a, b) => a.orderNo - b.orderNo);
            $questions = $questions;
            handleApiError();
        }
        questionToMove = undefined;
        destinationOrderNo = undefined;
    }

    function toggleAll(e) {
        const checked = e.detail.checked;
        $questions.forEach(q => q.isUiSelected = checked);
        $questions = $questions;
    }

    function formatQuestionText(question) {
        if (question.hasHtml) {
            return stripHtml(question.text);
        }

        return question.text;
    }

    function handleDragStarted(event) {
        setOriginalPositions();
        questionToMove = $questions.find(q => q.questionId === event.detail.itemId);
    }

    // we handle this just in case drag ends outside element which can receive a drop
    async function handleDragEnded(/*event*/) {
        await finalizeMove();
    }
    
    async function handleEnterDropArea(event) {
        const destinationQuestionId = event.detail.itemId;
        moveToTemp(destinationQuestionId);
    }

    function handleStartKeyMove(event) {
        setOriginalPositions();
        questionToMove = $questions.find(q => q.questionId === event.detail.itemId);
        reorderInfo = txt('Start_moving_element_x', [questionToMove.text]);
    }

    async function handleEndKeyMove(/*event*/) {
        const idMoved = questionToMove.questionId;
        await finalizeMove();
        const i = $questions.findIndex(q => q.questionId === idMoved);
        reorderInfo = txt('Element_has_been_moved_to_position_x_of_y', [i + 1, $questions.length]);
    }

    function handleKeyMoveUp(/*event*/) {
        const i = $questions.findIndex(q => q.questionId === questionToMove.questionId);
        if (i > 0) {
            const destinationQuestionId = $questions[i - 1].questionId;
            reorderInfo = txt('Moving_element_to_position_x_of_y', [i, $questions.length]);
            moveToTemp(destinationQuestionId);
        }
    }

    function handleKeyMoveDown(/*event*/) {
        const i = $questions.findIndex(q => q.questionId === questionToMove.questionId);
        if (i < $questions.length - 1) {
            const destinationQuestionId = $questions[i + 1].questionId;
            reorderInfo = txt('Moving_element_to_position_x_of_y', [i + 2, $questions.length]);
            moveToTemp(destinationQuestionId);
        }
    }

</script>



{#if errorFromApi}
    <Banner type="warning" text={warningText} close={txt('Close')} visible="{errorFromApi}" on:hidden={() => errorFromApi = false} />
{/if}
{#if hasGlobalResponse}
    <div class="question-information">
        <Banner type="info" text={txt('Not_possible_to_edit_questions_message')} visible="{true}" close={txt('Close')} />
    </div>
{/if}
<div class="toolbar">
    {#if enableModification}
        <div class="left"> 
            <IconButton text={txt('Add_question')} htmlType="button" type="primary" iconClass="add-question-button" on:click={() => navigateToEditQuestionPage("Open")} />
            {#if $questions && $questions.length > 0}
                <IconButton text={txt('Delete')} htmlType="button" type="secondary" iconClass="delete-questions-button" on:click={deleteSelectedQuestions} loading={isDeleting} />
            {/if}
        </div>
    {/if}
    {#if canPreviewSurvey}
        <div class="right">
            <Button text={txt('Preview_survey')} htmlType="button" type="secondary" on:click={() => { $currentQuestionIndex = 0; navigateToPreviewSurveyPage(hasGlobalResponse); }} />
        </div>
    {/if}
</div>

{#if !hasQuestions}
    <EmptyState 
        title={txt('Grid_no_questions_heading')} 
        description={txt('Grid_no_questions_text')}
        iconName="test-questions"
        >
    </EmptyState>
{:else}
    <div class="screen-reader" id="reorder_instructions">{txt('Move_element_instructions')}</div>
    <div class="screen-reader" aria-live="polite">{reorderInfo}</div>

    <div class="{enableModification ? 'questionlist-container' : 'questionlist-container--readonly'}">
        <List>
            <thead slot="header">
                <ListElement header>                        
                    {#if enableModification}
                        <Cell header type="dnd"></Cell>
                        <CheckboxCell bind:checked={allQuestionsSelected} small={true} text="{txt('Select_all_questions')}" header on:change={toggleAll} />
                    {/if}
                    <TextCell text="{txt('Question_number_header')}" header headerHidden={true} />                    
                    <TextCell text="{txt('Question')}" header />
                    <TextCell text="{txt('Question_type_short')}" header priority="2" />
                </ListElement>
            </thead>

            <tbody slot="body">
                {#each $questions as q, i (q.questionId)}
                    <ListElement dragged={questionToMove && q.questionId === questionToMove.questionId} checked={q.isUiSelected}>
                        {#if enableModification}
                            <Cell type="dnd">
                                <DragHandle 
                                    slot="data" 
                                    itemId="{q.questionId}"
                                    ariaDescribedById="reorder_instructions"
                                    on:dragStarted={handleDragStarted} 
                                    on:dragEnded={handleDragEnded} 
                                    on:dragEnterDropArea={handleEnterDropArea}
                                    on:startKeyMove={handleStartKeyMove}
                                    on:endKeyMove={handleEndKeyMove}
                                    on:keyMoveUp={handleKeyMoveUp}
                                    on:keyMoveDown={handleKeyMoveDown} />
                            </Cell>
                            <CheckboxCell bind:checked={q.isUiSelected} small={true} text="{txt('Select_question_x', [i + 1])}" />
                        {/if}
                        <TextCell text="{i + 1}" />
                        <Cell type="title">
                            <Link slot="data" href="#edit-question{q.questionId}" on:click={() => navigateToEditQuestionPage(q.type, q.questionId)} text={formatQuestionText(q)} />
                        </Cell>
                        <TextCell text="{shortQuestionTypeText(q.type)}" priority="2" />
                    </ListElement>
                {/each}
            </tbody>
        </List>
    </div>
{/if}

<Modal enabled={showNoQuestionsSelectedForDeleteAlert}
        titleText={txt('Select_at_least_one_question_to_delete')}
        confirmText={txt('Ok')}
        titleOnly={true}
        showCancel={false}
        on:cancel={() => {showNoQuestionsSelectedForDeleteAlert = false;}}
        on:close={() => {showNoQuestionsSelectedForDeleteAlert = false;}}
    />

<Modal enabled={showDeleteWarning} 
        titleText={txt('Delete_questions_modal_title')}
        cancelText={txt('Cancel')}
        confirmText={txt('Delete')}
        titleOnly={true}
        on:confirm={performDelete}
        on:cancel={() => {showDeleteWarning = false;}}
        on:close={() => {showDeleteWarning = false;}}
/>

